import { useState } from 'react'
import omit from 'lodash-es/omit'

import {
  createRelatedRequestsMassMailing,
  sendRequestTaskEmail,
} from '../../../../../../api'
import { EMPTY_LIST } from '../../../../../../constants/common'
import { useAsyncCallback } from '../../../../../../hooks'
import {
  EMAIL_CREATION_FORM_FIELD_NAME_BCC,
  EMAIL_CREATION_FORM_FIELD_NAME_CC,
  EMAIL_CREATION_FORM_FIELD_NAME_FROM,
  EMAIL_CREATION_FORM_FIELD_NAME_TO,
  useMassMailingAsyncAction,
} from '../../../../../Email'
import { RequestProfileTabEmailsAttachedRequestsRecipientsFilterModal } from '../../../RequestProfileTabEmailsAttachedRequestsRecipientsFilterModal'
import { AttachedRequestRecipientTag } from '../../AttachedRequestRecipientTag'
import {
  CREATE_EMAIL_POPUP_MODE_FIELD_NAME,
  CREATE_EMAIL_POPUP_MODE_STANDARD,
} from '../../RequestProfileTabEmailsCreateEmailPopUp.constants'
import {
  buildRecipientsByRequest,
  getRequestProfileCreateEmailPayload,
  getStandardFilterModalInitialValues,
  isRequestProfileCreateEmailModeAttachedRequests,
} from '../../RequestProfileTabEmailsCreateEmailPopUp.utils'

export function useRequestProfileTabEmailsCreateEmailForm({
  audience,
  initialValues,
  requestData,
  onClose,
  onSendSuccess,
}) {
  const [mode, setMode] = useState(
    initialValues?.[CREATE_EMAIL_POPUP_MODE_FIELD_NAME] ??
      CREATE_EMAIL_POPUP_MODE_STANDARD
  )

  const isAttachedRequestsMode =
    isRequestProfileCreateEmailModeAttachedRequests(mode)

  const {
    checkAsyncMassMailingTaskStatus,
    getAsyncTaskId,
    handleAddFinishMassMailingNotification,
  } = useMassMailingAsyncAction({
    onCloseModal: onClose,
    onReload: onSendSuccess,
  })

  function handleSuccess({ data }) {
    const taskId = getAsyncTaskId(data)

    if (taskId) {
      checkAsyncMassMailingTaskStatus(
        { ...data, task: taskId },
        handleAddFinishMassMailingNotification
      )
    } else {
      handleAddFinishMassMailingNotification(data)
    }
  }

  function handleModeChange(nextMode) {
    setMode(nextMode)
  }

  const handleSubmit = useAsyncCallback(
    (payload, values) => {
      const requestPayload = getRequestProfileCreateEmailPayload({
        audience,
        owner: requestData?.owner?.id,
        payload,
      })

      if (isAttachedRequestsMode) {
        return createRelatedRequestsMassMailing(requestData?.id, {
          ...omit(requestPayload, EMAIL_CREATION_FORM_FIELD_NAME_TO),
          recipients_by_request: buildRecipientsByRequest(
            values?.[EMAIL_CREATION_FORM_FIELD_NAME_TO] ?? EMPTY_LIST
          ),
        })
      }

      return sendRequestTaskEmail({ data: requestPayload, id: requestData?.id })
    },
    [audience, isAttachedRequestsMode, requestData?.id, requestData?.owner?.id]
  )

  function handleOpenAttachedRequestsFilterModal(
    _event,
    { onCloseModal, onSetModal }
  ) {
    onSetModal?.(
      <RequestProfileTabEmailsAttachedRequestsRecipientsFilterModal
        requestId={requestData?.id}
        onClose={onCloseModal}
      />
    )
  }

  const standardRecipientsFieldProps = {
    filterModalInitialValues: getStandardFilterModalInitialValues(requestData),
  }

  const fromFieldProps = { withServiceAccount: true }

  const fieldsProps = isAttachedRequestsMode
    ? {
        [EMAIL_CREATION_FORM_FIELD_NAME_FROM]: fromFieldProps,
        [EMAIL_CREATION_FORM_FIELD_NAME_TO]: {
          Tag: AttachedRequestRecipientTag,
          blocked: true,
          onOpenFilterModal: handleOpenAttachedRequestsFilterModal,
        },
      }
    : {
        [EMAIL_CREATION_FORM_FIELD_NAME_BCC]: standardRecipientsFieldProps,
        [EMAIL_CREATION_FORM_FIELD_NAME_CC]: standardRecipientsFieldProps,
        [EMAIL_CREATION_FORM_FIELD_NAME_FROM]: fromFieldProps,
        [EMAIL_CREATION_FORM_FIELD_NAME_TO]: standardRecipientsFieldProps,
      }

  return {
    fieldsProps,
    handleSubmit,
    handleSuccess,
    isAttachedRequestsMode,
    modeFieldProps: {
      mode,
      requestId: requestData?.id,
      onModeChange: handleModeChange,
    },
  }
}
